import { SITE_URL } from "./site";

export interface SeoInput {
  title: string;
  description: string;
  path: string; // canonical relativa, ej: "/activaciones-btl"
  image?: string; // og:image relativa o absoluta
  type?: "website" | "article" | "profile";
}

const OG_DEFAULT = "/og-default.png";

export function seoMeta({ title, description, path, image, type = "website" }: SeoInput) {
  const rawImage = image ?? OG_DEFAULT;
  const imageUrl = rawImage.startsWith("http") ? rawImage : absoluteUrl(rawImage);

  return [
    { title },
    { name: "description", content: description },
    // Open Graph
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: absoluteUrl(path) },
    { property: "og:locale", content: "es_PE" },
    { property: "og:site_name", content: "Improve Yourself" },
    { property: "og:image", content: imageUrl },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: title },
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: imageUrl },
  ] satisfies Array<Record<string, string>>;
}

export function absoluteUrl(path: string) {
  return `${SITE_URL}${path}`;
}

/** URL pública completa de una ruta interna (idempotente: una URL ya absoluta se deja igual). */
export function urlPublica(rutaOUrl: string) {
  if (/^https?:\/\//i.test(rutaOUrl)) return rutaOUrl;
  return absoluteUrl(rutaOUrl.startsWith("/") ? rutaOUrl : `/${rutaOUrl}`);
}

export function canonicalLink(path: string) {
  // urlPublica y no absoluteUrl: un canonical guardado como URL completa o sin barra inicial no
  // debe publicar «https://www.improvey.agencyhttps://…».
  return [{ rel: "canonical", href: urlPublica(path) }];
}

export function robotsMeta(index: boolean, follow: boolean) {
  const content = `${index ? "index" : "noindex"}, ${follow ? "follow" : "nofollow"}`;
  return [{ name: "robots", content }];
}

// Sin contenido local suficiente ni impresiones en 90 días. El editor SEO
// puede reactivar la indexación cuando se incorpore contenido local propio.
export function defaultRobotsIndex(path: string): boolean {
  return ![
    "/activaciones-btl/ate",
    "/activaciones-btl/barranco",
    "/activaciones-btl/callao",
    "/activaciones-btl/jesus-maria",
    "/activaciones-btl/la-molina",
    "/activaciones-btl/la-victoria",
    "/activaciones-btl/lince",
    "/activaciones-btl/san-borja",
    "/activaciones-btl/san-isidro",
    "/activaciones-btl/san-miguel",
    "/activaciones-btl/surco",
    "/activaciones-btl/surquillo",
    "/impulsadoras-promotoras/ate",
    "/impulsadoras-promotoras/barranco",
    "/impulsadoras-promotoras/callao",
    "/impulsadoras-promotoras/jesus-maria",
    "/impulsadoras-promotoras/la-molina",
    "/impulsadoras-promotoras/la-victoria",
    "/impulsadoras-promotoras/lince",
    "/impulsadoras-promotoras/los-olivos",
    "/impulsadoras-promotoras/miraflores",
    "/impulsadoras-promotoras/san-borja",
    "/impulsadoras-promotoras/san-isidro",
    "/impulsadoras-promotoras/san-miguel",
    "/impulsadoras-promotoras/surco",
    "/impulsadoras-promotoras/surquillo",
  ].includes(path);
}
